import React from 'react';
import { Card, Row, Col, Statistic } from 'antd';
import { ClockCircleOutlined, HourglassOutlined, ThunderboltOutlined, UsergroupDeleteOutlined } from '@ant-design/icons';
import {
  type BrevoSyncEjecucion,
  type BrevoSyncRitmoPunto,
  transcurridoSegundos,
  estimacionRestanteSegundos,
  formatDuracion,
} from '../../api/brevoSyncApi';
import RitmoSparkline from './RitmoSparkline';

interface StatCardsProps {
  ejecucion?: BrevoSyncEjecucion | null;
  ultimaFinalizada?: BrevoSyncEjecucion | null;
  ritmo?: BrevoSyncRitmoPunto[];
}

const StatCards: React.FC<StatCardsProps> = ({ ejecucion, ultimaFinalizada, ritmo }) => {
  const isRunning = !!ejecucion;
  const ref = ejecucion || ultimaFinalizada;

  if (!ref) return null;

  // Transcurrido
  let transcurrido: string;
  if (isRunning) {
    transcurrido = formatDuracion(transcurridoSegundos(ejecucion!));
  } else if (ref.fin) {
    transcurrido = formatDuracion(Math.floor((new Date(ref.fin).getTime() - new Date(ref.inicio).getTime()) / 1000));
  } else {
    transcurrido = '—';
  }

  // Estimación
  let estimacion: string;
  if (isRunning) {
    const restante = estimacionRestanteSegundos(ejecucion!);
    estimacion = restante != null ? `~${formatDuracion(restante)}` : 'Calculando…';
  } else {
    estimacion = 'Finalizada';
  }

  // Ritmo
  let ritmoValor: string;
  if (isRunning) {
    const elapsed = transcurridoSegundos(ejecucion!);
    const total = ejecucion!.faseAExitos + ejecucion!.faseBExitos;
    ritmoValor = elapsed > 0 ? `${Math.round(total / elapsed * 60)} c/min` : '—';
  } else {
    const dur = ref.fin ? Math.floor((new Date(ref.fin).getTime() - new Date(ref.inicio).getTime()) / 1000) : 0;
    const total = ref.faseAExitos + ref.faseBExitos;
    ritmoValor = dur > 0 ? `${Math.round(total / dur * 60)} c/min medio` : '—';
  }

  // Duplicados
  const duplicados = ref.duplicadosFusionados;

  return (
    <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
      <Col xs={12} lg={6}>
        <Card size="small" style={{ background: '#fafafa' }}>
          <Statistic title={<span><ClockCircleOutlined style={{ marginRight: 4 }} />Transcurrido</span>} value={transcurrido} />
        </Card>
      </Col>
      <Col xs={12} lg={6}>
        <Card size="small" style={{ background: '#fafafa' }}>
          <Statistic title={<span><HourglassOutlined style={{ marginRight: 4 }} />Estimación</span>} value={estimacion} />
        </Card>
      </Col>
      <Col xs={12} lg={6}>
        <Card size="small" style={{ background: '#fafafa' }}>
          <Statistic title={<span><ThunderboltOutlined style={{ marginRight: 4 }} />Ritmo</span>} value={ritmoValor} />
          {ritmo && ritmo.length >= 2 && (
            <div style={{ marginTop: 8 }}>
              <RitmoSparkline puntos={ritmo} width={200} height={40} color="#1890ff" />
            </div>
          )}
        </Card>
      </Col>
      <Col xs={12} lg={6}>
        <Card size="small" style={{ background: '#fafafa' }}>
          <Statistic title={<span><UsergroupDeleteOutlined style={{ marginRight: 4 }} />Duplicados</span>} value={duplicados} />
        </Card>
      </Col>
    </Row>
  );
};

export default StatCards;
